/**
 * Single source of truth for the project's own social presence.
 *
 * Only accounts that actually exist belong here. A footer full of dead links to
 * unclaimed handles reads worse than a footer with two live ones, and this site's
 * whole pitch is that its claims check out. Verified 2026-08-31:
 *   YouTube  live  (21 videos)
 *   TikTok   live
 *   Reddit   believed live, but reddit.com blocks server-side checks — gated
 *            behind `verified` until someone confirms in a browser.
 *   Instagram live — @hospitalprices, added 2026-09-04 on Master's say-so.
 *            instagram.com answers every logged-out datacenter request with
 *            "Page isn't available" + a login redirect, so it can't be
 *            re-checked from the server either; confirm in a browser.
 *   X / Bluesky / Facebook — handles reserved but NOT set up
 *            (x.com/AHPTransparency returns 404), so they stay out.
 *
 * To add one: flip `verified: true` and it appears everywhere the footer and the
 * Follow page render. Nothing else to touch.
 */

export type SocialLink = {
  id: string;
  label: string;
  handle: string;
  href: string;
  /** Only rendered when true — see note above. */
  verified: boolean;
  /** Brand colour, used for hover only; the resting state stays monochrome. */
  color: string;
  /** Inline SVG path data, 24x24 viewBox. lucide has no TikTok/Reddit glyph. */
  path: string;
};

export const SOCIAL_LINKS: SocialLink[] = [
  {
    id: "youtube",
    label: "YouTube",
    handle: "@americanhospitalprices",
    href: "https://www.youtube.com/@americanhospitalprices",
    verified: true,
    color: "#FF0000",
    path: "M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.5 15.6V8.4l6.3 3.6z",
  },
  {
    id: "tiktok",
    label: "TikTok",
    handle: "@americanhospitalprices",
    href: "https://www.tiktok.com/@americanhospitalprices",
    verified: true,
    color: "#000000",
    path: "M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .77-5.06V9.68a5.67 5.67 0 0 0-.77-.05A5.67 5.67 0 1 0 15.54 15.3V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48z",
  },
  {
    id: "instagram",
    label: "Instagram",
    handle: "@hospitalprices",
    href: "https://www.instagram.com/hospitalprices/",
    verified: true,
    color: "#E4405F",
    // Rounded-square ring + lens ring + dot; inner shapes wound opposite so it
    // fills correctly under both nonzero and evenodd.
    path: "M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm5.25-3.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z",
  },
  {
    id: "reddit",
    label: "Reddit",
    handle: "r/AmericanHealthPrices",
    href: "https://www.reddit.com/r/AmericanHealthPrices/",
    verified: false, // flip once confirmed in a browser
    color: "#FF4500",
    path: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm5.2 11.3a1.4 1.4 0 0 1 .02.26c0 2.25-2.62 4.07-5.85 4.07s-5.85-1.82-5.85-4.07a1.4 1.4 0 0 1 .02-.26 1.5 1.5 0 1 1 1.73-2.4 7.2 7.2 0 0 1 3.9-1.24l.74-3.48a.3.3 0 0 1 .36-.23l2.44.52a1.05 1.05 0 1 1-.12.6l-2.14-.46-.66 3.1a7.2 7.2 0 0 1 3.8 1.23 1.5 1.5 0 1 1 1.6 2.36zM9.1 13.4a1.05 1.05 0 1 0 0-2.1 1.05 1.05 0 0 0 0 2.1zm5.8 0a1.05 1.05 0 1 0 0-2.1 1.05 1.05 0 0 0 0 2.1zm-.3 2.06a.35.35 0 0 1 .02.5 4.1 4.1 0 0 1-2.62.8 4.1 4.1 0 0 1-2.62-.8.35.35 0 1 1 .48-.5 3.4 3.4 0 0 0 2.14.6 3.4 3.4 0 0 0 2.14-.6.35.35 0 0 1 .46 0z",
  },
];

/** The links actually safe to render. */
export const liveSocialLinks = () => SOCIAL_LINKS.filter((s) => s.verified);
